"use client";

import { useEffect, useState, useRef } from "react";
import { usePathname } from "next/navigation";

// "dismissed" | "installed" — permanent, never show again
const PERM_KEY    = "blackdoor_pwa_v2";
// "1" = shown on landing, "2" = shown on home — session-scoped
const SESSION_KEY = "blackdoor_pwa_seen";

type Platform = "ios" | "android" | "other";

function getPlatform(): Platform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "other";
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

export default function PWAInstallBanner() {
  const pathname = usePathname();
  const [visible,        setVisible]        = useState(false);
  const [platform,       setPlatform]       = useState<Platform>("other");
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // One-time: detect platform + capture Android/Chrome install prompt
  useEffect(() => {
    if (isStandalone() || localStorage.getItem(PERM_KEY)) return;

    const p = getPlatform();
    setPlatform(p);
    if (p === "other") return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  // Show logic: landing page after 2s, home page if not yet dismissed
  useEffect(() => {
    if (platform === "other") return;
    if (isStandalone() || localStorage.getItem(PERM_KEY)) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);

    const seen = sessionStorage.getItem(SESSION_KEY); // null | "1" | "2"

    if (pathname === "/" && !seen) {
      // First visit to landing — show after 2 seconds
      timerRef.current = setTimeout(() => {
        if (!localStorage.getItem(PERM_KEY)) {
          sessionStorage.setItem(SESSION_KEY, "1");
          setVisible(true);
        }
      }, 2000);
    } else if (pathname === "/home" && seen === "1") {
      // User came from landing without dismissing — give them a second chance on /home
      timerRef.current = setTimeout(() => {
        if (!localStorage.getItem(PERM_KEY)) {
          sessionStorage.setItem(SESSION_KEY, "2");
          setVisible(true);
        }
      }, 700);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pathname, platform]);

  function dismiss() {
    localStorage.setItem(PERM_KEY, "dismissed");
    sessionStorage.removeItem(SESSION_KEY);
    setVisible(false);
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        localStorage.setItem(PERM_KEY, "installed");
        sessionStorage.removeItem(SESSION_KEY);
      }
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
    }
  }

  if (!visible) return null;

  const containerStyle: React.CSSProperties = {
    bottom: "calc(1rem + env(safe-area-inset-bottom, 0px))",
  };

  // ─── iOS: inline step-by-step guide ─────────────────────────────────────────
  if (platform === "ios") {
    return (
      <div
        className="pwa-banner-enter fixed inset-x-4 z-[9999] flex justify-center"
        style={containerStyle}
      >
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0c0c18]/96 backdrop-blur-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-white/6">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/icon-192.png" alt="" width={36} height={36} className="rounded-xl shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-black text-white leading-tight">Add to Home Screen</p>
              <p className="text-[11px] text-white/35 leading-tight mt-0.5">Get the full BlackDoor experience</p>
            </div>
            <button
              onClick={dismiss}
              aria-label="Close"
              className="h-7 w-7 shrink-0 rounded-full bg-white/8 flex items-center justify-center text-white/40 hover:text-white/60 hover:bg-white/12 transition-colors text-[11px] font-bold"
            >
              ✕
            </button>
          </div>
          {/* Steps */}
          <div className="px-4 py-4 space-y-3">
            {[
              "Tap the Share button at the bottom of Safari",
              'Scroll down and tap "Add to Home Screen"',
              'Tap "Add" in the top-right corner',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="h-5 w-5 shrink-0 mt-0.5 rounded-full bg-white/8 flex items-center justify-center text-[10px] font-black text-white/50">
                  {i + 1}
                </span>
                <p className="text-xs text-white/60 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Android / Chrome: native install prompt ─────────────────────────────────
  return (
    <div
      className="pwa-banner-enter fixed inset-x-4 z-[9999] flex justify-center"
      style={containerStyle}
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0c0c18]/96 backdrop-blur-xl shadow-2xl">
        <div className="flex items-center gap-3.5 px-4 py-3.5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icon-192.png" alt="" width={40} height={40} className="rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-black text-white leading-tight">BlackDoor</p>
            <p className="text-[11px] text-white/35">Add to your home screen</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {deferredPrompt ? (
              <button
                onClick={handleInstall}
                className="rounded-xl bg-white text-black text-[11px] font-black px-3.5 py-1.5 hover:bg-white/90 active:scale-95 transition-all"
              >
                Add
              </button>
            ) : (
              <span className="text-[10px] text-white/30 max-w-[110px] text-right leading-tight">
                Open in Chrome to install
              </span>
            )}
            <button
              onClick={dismiss}
              aria-label="Close"
              className="h-7 w-7 rounded-full bg-white/6 flex items-center justify-center text-white/30 hover:text-white/60 hover:bg-white/10 transition-all text-[11px] font-bold"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
